import { useState } from "react";
import { Button, Text, Modal, Paper } from "@mantine/core";
import "./ClientPaymentRequests.scss";
import { IconArrowRight, IconCash, IconCashOff } from "@tabler/icons-react";
import { useStripePayments } from "@/utils/hooks/useStripePayments";
import PaymentTable from "../TaxProPaymentRequests/PaymentTable/PaymentTable";
import { makePaymentButton, paymentModalTitle } from "../utils";
import { createCheckoutSession } from "@/utils/api/payments";

interface PaymentRequest {
  id: string;
  amount: number;
  description: string;
  createdAt: string;
  status: string;
}

const ClientPaymentRequests = () => {
  const [modalOpened, setModalOpened] = useState(false);

  const personalInfo = JSON.parse(localStorage.getItem("personalInfo") ?? "{}");
  const clientId = personalInfo?.userId;
  const params = { client_id: clientId };
  const { data } = useStripePayments(params);

  // Filter for pending payment requests, then take the two most recent
  const recentPayments: PaymentRequest[] =
    data?.content?.paymentRequests
      ?.filter((payment: PaymentRequest) => payment.status === "pending")
      .slice(0, 2) || [];

  const [loadingPaymentId, setLoadingPaymentId] = useState<string | null>(null);

  const handleMakePayment = async (paymentRequestId: string) => {
    setLoadingPaymentId(paymentRequestId);

    try {
      const response = await createCheckoutSession(paymentRequestId);
      const checkoutUrl = response?.content?.checkoutUrl;

      if (!checkoutUrl) {
        console.error("No checkout URL returned from the server.");
        setLoadingPaymentId(null);
        return;
      }

      window.location.assign(checkoutUrl); // need assign() to avoid race conditions
    } catch (err) {
      console.error("Error creating checkout session:", err);
      setLoadingPaymentId(null);
    }
  };

  return (
    <div className="payment-requests">
      <div className="payment-header">
        <IconCash size={20} />
        <p>Payment Requests</p>
      </div>

      <Text c="dimmed">
        Securely pay your tax professional using their payment platform
      </Text>

      <div className="payment-requests-container">
        <div className="payment-request-items">
          {recentPayments.length > 0 ? (
            recentPayments.map((payment: PaymentRequest) => (
              <div key={payment.id} className="payment-request-item">
                <div className="payment-request-info">
                  <Text size="lg" fw={700}>
                    ${Number(payment.amount) / 100}
                  </Text>
                  <Text c="dimmed">{payment.description}</Text>
                  <Text c="dimmed">
                    Requested {new Date(payment.createdAt).toLocaleDateString()}
                  </Text>
                </div>
                {makePaymentButton(
                  () => handleMakePayment(payment.id),
                  loadingPaymentId === payment.id
                )}
              </div>
            ))
          ) : (
            <div className="no-payment-requests">
              <IconCashOff size={40} color="lightgray" />
              <Text c="dimmed">No recent pending payment requests</Text>
            </div>
          )}

          <div className="payment-request-activity">
            <Button
              variant="transparent"
              rightSection={<IconArrowRight size={16} />}
              onClick={() => setModalOpened(true)}
            >
              <Text fw={600} c="#0077cc">
                View payment activity
              </Text>
            </Button>
          </div>
        </div>
      </div>

      {/* Mantine Modal */}
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        title={paymentModalTitle("Payment Activity")}
        size="xxl"
      >
        <Text size="sm" c="dimmed" ml={"lg"} mb={"lg"}>
          Effortlessly send and track payments to your tax professional. For
          refund requests, please contact your tax professional.
        </Text>
        <Paper withBorder p="lg">
          <PaymentTable isOwner={false} />
        </Paper>
      </Modal>
    </div>
  );
};

export default ClientPaymentRequests;
